import React, { useMemo } from 'react';
import { getEventTypeConfig } from '@config';
import { Seo } from '@shared/components/Seo';
import { generateDescriptionKeywords } from '@shared/utils/seo';
import { EventType } from '@graphql/generated';
import { useTranslation } from '@shared/core';

/**
 * Adds a query param to the image url to optimize the image from Media Service and add a source query param
 */
const addOptimizationQueryParam = (url: Maybe<string>): string | undefined => {
  if (!url) return undefined;
  const hasQueryParam = url.includes('?');
  const separator = hasQueryParam ? '&' : '?';
  return `${url}${separator}rendition=small&source=registrypreview`;
};

type PageData = {
  id: string;
  disabled: boolean;
  type: string;
  pageSlug: string;
};

interface GuestRegistryHeadProps
  extends Readonly<{
    handle: Maybe<string>;
    eventDisplayName: Maybe<string>;
    imageUrl: Maybe<string>;
    ownerFirstName: Maybe<string>;
    ownerLastName: Maybe<string>;
    fianceeFirstName: Maybe<string>;
    pageData: PageData[];
    eventType: EventType;
  }> {}

export const GuestRegistryHead: React.FC<GuestRegistryHeadProps> = props => {
  const { handle, eventDisplayName, imageUrl, ownerFirstName, ownerLastName, fianceeFirstName, pageData, eventType } = props;
  const { t } = useTranslation('websitePages');
  const keywords = useMemo(() => generateDescriptionKeywords(t, pageData), [t, pageData]);

  const userInfo = useMemo(
    () => ({
      partnerOneFirstName: ownerFirstName || '',
      partnerOneLastName: ownerLastName || '',
      partnerTwoFirstName: fianceeFirstName || '',
    }),
    [ownerFirstName, ownerLastName, fianceeFirstName],
  );

  const pageTitle = useMemo(() => getEventTypeConfig(eventType)?.guestSitePageTitle(eventDisplayName || '', userInfo, t) || '', [eventType, eventDisplayName, userInfo, t]);

  const pageDescription = useMemo(
    () => getEventTypeConfig(eventType)?.guestSitePageDescription(eventDisplayName || '', userInfo, keywords, t),
    [eventType, eventDisplayName, userInfo, keywords, t],
  );
  const pageUrl = `https://withjoy.com/${handle}/`;

  return (
    <Seo
      title={pageTitle}
      description={pageDescription}
      canonicalURL={pageUrl}
      name="Joy"
      url={pageUrl}
      ogImage={addOptimizationQueryParam(imageUrl)}
      author="@JoytheApp"
      twitterSite="@JoytheApp"
    />
  );
};
